import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/EditOutlined';
import {
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import EditModal from 'components/shared/EditModal';
import { privateClient } from '../../axiosClients/axiosClients';
import {
  deleteCostImpactFile,
  getCostImpactFilesQueryOptions,
} from '../../queries/fileQueries';
import {
  getCostImpactOnProductsQueryOptions,
  updateCostImpactOnProducts,
} from '../../queries/SummaryQueries';
import { deleteFile, TextAsLink } from '../../styles';
import { getDownloadLink } from '../../utils';
import ConfirmModal from '../shared/ConfirmModal';
import UploadCostImpact from './UploadCostImpact';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#1F305E',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: '16px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '14px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function CostImpact(props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newCostImpact, setNewCostImpact] = useState(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: deleteCostImpactFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-impact'] });
      setIsOpen(false);
    },
  });
  const { data, isLoading, error } = useQuery(
    getCostImpactFilesQueryOptions(
      { pecrId: props.pecrId, siteId: props.siteId },
      {}
    )
  );
  const { data: costImpactData, error: costImpactError } = useQuery(
    getCostImpactOnProductsQueryOptions(
      { pecrId: props.pecrId, siteId: props.siteId },
      { select: data => data.data }
    )
  );

  const { mutate: updateCostImpact } = useMutation({
    mutationFn: updateCostImpactOnProducts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-cost-impact'] });
      setIsEditOpen(false);
    },
    onError: error => {
      console.error('Error updating cost impact:', error);
    },
  });
  const onDownloadCostImpactClick = async filename => {
    try {
      const response = await privateClient.post(
        '/DownloadFilesForCostImpact',
        {
          MainFolder: 'PECROverview',
          SubFolder: props.pecrId,
          siteId: props.siteId,
          costImpact: 'CostImpact',
          filename,
        },
        {
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      getDownloadLink(blob, filename);
    } catch (error) {
      console.log('error downloading cost impact file:', error);
    }
  };
  return (
    <Box sx={{ marginBottom: '0.5rem' }}>
      <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 800 }}>
        <Table sx={{ width: '100%' }} aria-label="cost impact table">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t('Cost_impact_on_products')}</StyledTableCell>
              <StyledTableCell align="center">{t('Files')}</StyledTableCell>
              <StyledTableCell align="center">
                {t('Upload_Documents')}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StyledTableRow>
              <StyledTableCell
                component="th"
                scope="row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 'bold', color: '#1F305E' }}
                >
                  {costImpactData && costImpactData.costImpactOnProducts
                    ? costImpactData.costImpactOnProducts
                    : 'N/A'}
                </Typography>
                {costImpactData && costImpactData.hasCostImpact && (
                  <EditIcon
                    fontSize="small"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setIsEditOpen(true);
                    }}
                  />
                )}
                <EditModal
                  open={isEditOpen}
                  title="Edit Cost Impact"
                  message="Enter the new cost impact value for this site."
                  setNewCostImpact={setNewCostImpact}
                  onConfirm={() =>
                    updateCostImpact({
                      pecrId: props.pecrId,
                      siteId: props.siteId,
                      costImpact: newCostImpact,
                    })
                  }
                  onCancel={() => setIsEditOpen(false)}
                />
              </StyledTableCell>
              <StyledTableCell align="center">
                {data && data.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {data.map((document, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'between',
                          gap: '8px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0',
                          },
                        }}
                      >
                        <DeleteIcon
                          fontSize="small"
                          style={deleteFile}
                          onClick={() => {
                            setIsOpen(true);
                            setSelectedFile(document);
                          }}
                        />
                        <ConfirmModal
                          title="Delete Cost Impact File"
                          message="Are you sure you want to delete this item?"
                          open={isOpen}
                          onConfirm={() =>
                            mutate({
                              fileName: selectedFile,
                              pecrId: props.pecrId,
                              siteId: props.siteId,
                              email: props.userInfo.userEmail,
                            })
                          }
                          onCancel={() => setIsOpen(false)}
                        />
                        <span>📁</span>
                        <Tooltip
                          title={`Click to download ${document}`}
                          placement={index % 2 === 0 ? 'left' : 'right'}
                          componentsProps={{
                            tooltip: { sx: { fontSize: '14px' } },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              ...TextAsLink,
                              cursor: 'pointer',
                              textAlign: 'center',
                            }}
                            onClick={() => onDownloadCostImpactClick(document)}
                          >
                            {document}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No files available
                  </Typography>
                )}
              </StyledTableCell>
              <StyledTableCell align="center">
                <UploadCostImpact pecrId={props.pecrId} siteId={props.siteId} />
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

CostImpact.propTypes = {
  pecrId: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired,
};
const mapStateToProps = (state = {}) => {
  return {
    userInfo: state.userInfo,
  };
};

export default connect(mapStateToProps, null)(CostImpact);
